import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { getSocket } from '../services/socket';
import { chatSocket } from '../services/socket';
import '../styles/Chat.css';

function Chat({ userId, userName, onClose, selectedFriendId }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [partnerIsTyping, setPartnerIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    // Reset scroll heuristics when switching conversations so the UI doesn't "jump"
    // from Loading -> Messages with a visible smooth scroll.
    prevMessageCountRef.current = 0;
  }, [selectedConversation?.other_user_id]);

  // Fetch conversations on mount
  useEffect(() => {
    console.log('💬 Chat component mounted, fetching conversations...');
    fetchConversations();
    const interval = setInterval(fetchConversations, 3000);
    
    // Listen for typing indicators
    chatSocket.onUserTyping(({ fromUserId }) => {
      if (selectedConversation?.other_user_id === fromUserId) {
        setPartnerIsTyping(true);
      }
    });

    chatSocket.onUserStoppedTyping(({ fromUserId }) => {
      if (selectedConversation?.other_user_id === fromUserId) {
        setPartnerIsTyping(false);
      }
    });

    return () => {
      clearInterval(interval);
      chatSocket.removeTypingListeners();
    };
  }, [selectedConversation]);

  // Auto-select friend if selectedFriendId is provided
  useEffect(() => {
    if (selectedFriendId) {
      const normalizedSelectedId = Number(selectedFriendId);
      const friendConversation = conversations.find(c => Number(c.other_user_id) === normalizedSelectedId);
      if (friendConversation) {
        handleSelectConversation(friendConversation);
      } else if (normalizedSelectedId) {
        const fallbackConversation = {
          other_user_id: normalizedSelectedId,
          username: 'Friend',
          last_message: '',
          unread_count: 0,
        };
        setSelectedConversation(fallbackConversation);
        fetchMessages(normalizedSelectedId);
      }
    }
  }, [selectedFriendId, conversations]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onNewMessage = ({ message }) => {
      if (!message) return;
      const otherUserId = Number(message.sender_id) === Number(userId)
        ? Number(message.recipient_id)
        : Number(message.sender_id);

      setConversations((prev) => {
        const existing = Array.isArray(prev) ? [...prev] : [];
        const index = existing.findIndex((item) => Number(item.other_user_id) === otherUserId);
        const conversationPatch = {
          other_user_id: otherUserId,
          username: Number(message.sender_id) === Number(userId)
            ? (existing[index]?.username || message.recipient_username || 'Friend')
            : (message.sender_username || existing[index]?.username || 'Friend'),
          last_message: message.content,
          unread_count: Number(message.sender_id) === Number(userId)
            ? 0
            : (Number(existing[index]?.unread_count || 0) + 1),
        };

        if (index >= 0) {
          existing[index] = { ...existing[index], ...conversationPatch };
          const [item] = existing.splice(index, 1);
          return [item, ...existing];
        }

        return [conversationPatch, ...existing];
      });

      if (Number(selectedConversation?.other_user_id) === otherUserId) {
        setMessages((prev) => [...prev, message]);
      }
    };

    socket.on('chat:message:new', onNewMessage);
    return () => socket.off('chat:message:new', onNewMessage);
  }, [selectedConversation?.other_user_id, userId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    const prev = prevMessageCountRef.current;
    const next = Array.isArray(messages) ? messages.length : 0;
    const behavior = (prev > 0 && next > prev) ? 'smooth' : 'auto';
    messagesEndRef.current?.scrollIntoView({ behavior });
    prevMessageCountRef.current = next;
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await api.get('/chat/conversations');
      console.log('📬 Conversations fetched:', res.data);
      setConversations(res.data || []);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      setLoading(true);
      const res = await api.get(`/chat/conversations/${otherUserId}`);
      setMessages(res.data || []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.other_user_id);
    // Mark messages as read
    api.get(`/chat/conversations/${conversation.other_user_id}`).catch(err => console.error('Failed to mark as read:', err));
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) {
      console.log('Cannot send: message empty or no conversation selected');
      return;
    }

    console.log('📤 Sending message:', newMessage);

    try {
      const message = await api.post('/chat/messages', {
        recipientId: selectedConversation.other_user_id,
        content: newMessage
      });

      console.log('✓ Message sent:', message.data);
      setMessages([...messages, message.data]);
      setNewMessage('');
      // Stop typing indicator
      if (selectedConversation) {
        chatSocket.stopTyping(selectedConversation.other_user_id);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleMessageInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Emit typing event if user has text
    if (selectedConversation && value.trim()) {
      chatSocket.startTyping(selectedConversation.other_user_id);

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Stop typing after 3 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        chatSocket.stopTyping(selectedConversation.other_user_id);
      }, 3000);
    } else if (selectedConversation) {
      // Stop typing if message is cleared
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      chatSocket.stopTyping(selectedConversation.other_user_id);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleBackToConversations = () => {
    setSelectedConversation(null);
    setPartnerIsTyping(false);
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h2>💬 Messages</h2>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      <div className={`chat-content ${selectedConversation ? 'has-selection' : ''}`}>
        {/* Conversations List */}
        <div className="conversations-list">
          <div className="conversations-header">
            <h3>Conversations</h3>
            <button 
              className="refresh-btn"
              onClick={fetchConversations}
              title="Refresh conversations"
            >
              🔄
            </button>
          </div>
          {conversations.length === 0 ? (
            <div className="no-conversations">
              <p>No messages yet</p>
              <p className="hint">Start a conversation with a friend!</p>
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.other_user_id}
                className={`conversation-item ${selectedConversation?.other_user_id === conv.other_user_id ? 'active' : ''}`}
                onClick={() => handleSelectConversation(conv)}
              >
                <div className="conversation-info">
                  <span className="user-name">{conv.username}</span>
                  <span className="last-message">{conv.last_message}</span>
                </div>
                {conv.unread_count > 0 && (
                  <span className="unread-badge">{conv.unread_count}</span>
                )}
              </div>
            ))
          )}
        </div>

        {/* Messages Area */}
        <div className="messages-area">
          {selectedConversation ? (
            <>
              <div className="messages-header">
                <div className="header-content">
                  <button
                    type="button"
                    className="mobile-back-btn"
                    onClick={handleBackToConversations}
                  >
                    ← Back
                  </button>
                  <h3>{selectedConversation.username}</h3>
                  {partnerIsTyping && (
                    <p className="typing-indicator">✏️ {selectedConversation.username} is typing...</p>
                  )}
                </div>
              </div>

              <div className="messages-list">
                {messages.length === 0 && loading ? (
                  <div className="loading">Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div className="no-messages">
                    <p>No messages yet</p>
                    <p className="hint">Start the conversation!</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`message ${msg.sender_id === userId ? 'sent' : 'received'}`}
                    >
                      <div className="message-content">
                        <p>{msg.content}</p>
                        <span className="timestamp">
                          {new Date(msg.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  ))
                )}

                {loading && messages.length > 0 && (
                  <div className="messages-loading-overlay" aria-live="polite">
                    <div className="messages-loading-overlay__chip">Loading…</div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="message-input-area">
                <textarea
                  className="message-input"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={handleMessageInputChange}
                  onKeyPress={handleKeyPress}
                  rows="2"
                />
                <button
                  className="send-btn"
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                >
                  Send
                </button>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>Select a conversation to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Chat;
